import { useEffect, useRef, useState, type FormEvent } from 'react';
import { AiSphere, useAiScope } from '../assistant/AiButton';
import { Reorder, motion, useDragControls } from 'motion/react';
import { ActLink } from '../ui/ActLink';
import { spring } from '../lib/motion';
import { DrawnCheck, Glide, Orb } from '../ui/motion';
import { BriefPanel } from '../ui/BriefPanel';
import { AssistantAsks, CheckIns } from '../ui/ActivityAi';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ACTIVITY_STATUSES, STATUS_LABELS, TYPE_LABELS, can, type ActivityDetail, type ActivityPatch, type ActivityStatus, type SessionUser, type Step } from '@dpw/shared';
import { api } from '../api';
import { useActivity, useActivityFiles, useChange, useSpace } from '../lib/queries';
import { Avatar, Diamond, ErrorNote, Loading, Progress, StatusPill, Toggle } from '../ui/bits';
import { IconClip, IconDown, IconUp, IconX } from '../ui/icons';
import { ActivityForm, type ActivityFormValue } from '../ui/ActivityForm';
import { FileList } from '../ui/FileList';
import { AddReminderForm } from '../ui/AddReminderForm';
import { Sheet } from '../ui/Sheet';
import { useToast } from '../ui/toast';
import { ago, dayLabel, localDay, localTime } from '../lib/format';
import { usePhone } from '../lib/media';
import { PhoneActivity } from '../phone/Activity';
import { useAssistantPanel } from '../assistant/Panel';
import { NewActivity } from '../ui/NewActivity';
import { SubActivities } from '../ui/SubActivities';
import { MoreMenu } from '../ui/Menu';
import { NewDot } from '../ui/NewDot';
import { useMarkSeen, useNewSince } from '../lib/seen';

/** The kind's colour, as the calendar and Today draw it. */
const KIND_COLOR = { meeting: 'var(--t-meet)', workshop: 'var(--t-work)', deliverable: 'var(--t-deliv)', task: 'var(--t-task)' } as const;

/** A change to the steps that is on screen before the server has it. */
type StepOp = { kind: 'done'; id: string; done: boolean } | { kind: 'order'; ids: string[] };

/** The activity with changes still being saved laid over it, so a refetch that does not have them yet cannot undo them. */
function withPending(a: ActivityDetail, ops: StepOp[]): ActivityDetail {
  if (!ops.length) return a;
  let steps = a.steps;
  for (const op of ops) {
    if (op.kind === 'done') steps = steps.map(st => (st.id === op.id ? { ...st, done: op.done, doneAt: op.done ? (st.doneAt ?? new Date().toISOString()) : null } : st));
    else {
      // Steps added meanwhile keep their place at the end; steps removed meanwhile drop out.
      const rank = new Map(op.ids.map((x, i) => [x, i]));
      steps = [...steps].sort((p, q) => (rank.get(p.id) ?? Infinity) - (rank.get(q.id) ?? Infinity));
    }
  }
  const done = steps.filter(st => st.done).length;
  return { ...a, steps, stepsDone: done, progress: steps.length ? Math.round(100 * done / steps.length) : 0 };
}

/** A meeting or workshop within three days whose prep is less than half ready. */
const prepAtRisk = (a: ActivityDetail) => a.needsPrep && !!a.startsAt && a.status !== 'done' && a.stepsTotal > 0 && a.stepsDone * 2 < a.stepsTotal
  && new Date(a.startsAt).getTime() - Date.now() < 3 * 86_400_000 && new Date(a.startsAt).getTime() > Date.now();

const redStatus = (a: ActivityDetail) => a.status === 'at_risk' || a.status === 'blocked' || a.isOverdue;

function when(a: ActivityDetail) {
  if (a.startsAt) return `${dayLabel(localDay(a.startsAt))} · ${localTime(a.startsAt)}${a.endsAt ? ` to ${localTime(a.endsAt)}` : ''}`;
  return 'Not scheduled';
}

export function ActivityPage({ me }: { me: SessionUser }) {
  const { id = '' } = useParams();
  return <ActivityView me={me} id={id} />;
}

/**
 * One activity: the full page (/activities/:id), or the activity panel (panel: docked on the right, or a sheet), with
 * the same tabs and the same editing. In the panel its tab is its own (the page's address is the page behind it), it
 * has Open full page and ×, and its AI button asks about this activity.
 *
 * 6.3 (Kareem, 5 Nov: "too much AI", and design bugs): the AI button is the violet sphere alone (the panel's header;
 * on the full page the shell's, in line with the actions); no "Tell the AI" box in "What the AI knows about this"
 * (asking is the sphere); one crumb line (kind, then Space › Track); Remind, Edit and a "more" menu with Delete (and a
 * confirm); the facts as a compact row. Opening it marks it seen (its blue "new" dot goes), and each tab clears its own.
 */
export function ActivityView({ me, id, panel: inPanel = false, onClose }: { me: SessionUser; id: string; panel?: boolean; onClose?: () => void }) {
  const panel = useAssistantPanel();
  // Set once this activity is deleted here, so nothing fetches it again while the page leaves.
  const [gone, setGone] = useState(false);
  const q = useActivity(id, !gone);
  useAiScope(!inPanel && q.data ? { spaceId: q.data.spaceId, trackId: q.data.trackId, activityId: q.data.id, label: q.data.title, sphere: true } : null, !inPanel);
  const files = useActivityFiles(id, !gone);
  const space = useSpace(q.data?.spaceId ?? '');
  const [params, setParams] = useSearchParams();
  type Tab = 'overview' | 'updates' | 'minutes' | 'files';
  const [ownTab, setOwnTab] = useState<Tab>('overview');
  const tab: Tab = inPanel ? ownTab : (['overview', 'updates', 'minutes', 'files'] as const).find(t => t === params.get('tab')) ?? 'overview';
  const setTab = (t: Tab) => (inPanel ? setOwnTab(t) : setParams(t === 'overview' ? {} : { tab: t }, { replace: true }));
  const [drop, setDrop] = useState(false);
  const [edit, setEdit] = useState(false);
  const [remind, setRemind] = useState(false);
  // A new sub-activity with all its details (the full form, "Part of" set to this one).
  const [subNew, setSubNew] = useState(false);
  const [stepDraft, setStepDraft] = useState('');
  const [update, setUpdate] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  const nav = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const phone = usePhone();
  // In the panel the focus moves into it once it has the activity (so Escape closes it, and Tab goes through it).
  const root = useRef<HTMLDivElement>(null);
  const loaded = !!q.data;
  useEffect(() => { if (inPanel && loaded && !phone) root.current?.focus({ preventScroll: true }); }, [inPanel, loaded, phone]);
  // New since you looked: opening it is looking at it (again whenever something new arrives while it is open); a tab
  // with something new is seen once it is open.
  const dots = useNewSince();
  const markSeen = useMarkSeen();
  const rowNew = dots.row(id);
  const tabNew = dots.tabs(id);
  useEffect(() => { if (loaded && !gone) markSeen('activity', id); }, [loaded, id, rowNew, gone, markSeen]);
  const thisTabNew = tab !== 'overview' && tabNew[tab];
  useEffect(() => { if (loaded && thisTabNew) markSeen(tab, id); }, [loaded, thisTabNew, tab, id, markSeen]);
  const [askDelete, setAskDelete] = useState(false);

  // Every change is made against the version on screen; if someone changed it meanwhile the server says so (409).
  const patch = useChange((p: ActivityPatch & { version?: number }) => {
    const { version, ...rest } = p;
    return api.activities.update(id, rest, version ?? q.data!.version);
  });
  const [editVersion, setEditVersion] = useState(0);
  // Ticks and moves show at once and stay on screen while they save, whatever refetches meanwhile: each is laid
  // over the server's copy until a refetch after the save has it. If the server says no, it comes back off.
  const [pending, setPending] = useState<{ n: number; op: StepOp }[]>([]);
  const [stepErr, setStepErr] = useState<unknown>(null);
  const opNo = useRef(0);
  const runStep = (op: StepOp) => {
    const n = ++opNo.current;
    setStepErr(null);
    setPending(p => [...p, { n, op }]);
    const drop = () => setPending(p => p.filter(x => x.n !== n));
    const save = op.kind === 'done' ? api.activities.updateStep(op.id, { done: op.done }) : api.activities.reorderSteps(id, op.ids);
    save.then(
      () => qc.invalidateQueries({ queryKey: ['activity', id] }).then(drop, drop).then(() => qc.invalidateQueries()),
      (e: unknown) => { drop(); setStepErr(e); },
    );
  };
  // While a step is being dragged, its order is the hand's, not the server's.
  const [dragOrder, setDragOrder] = useState<string[] | null>(null);
  const dragRef = useRef<string[] | null>(null);
  dragRef.current = dragOrder;
  const addStep = useChange((t: string) => api.activities.addStep(id, t));
  const delStep = useChange((stepId: string) => api.activities.deleteStep(stepId));
  const post = useChange((b: string) => api.activities.addUpdate(id, b));
  const upload = useChange((f: File) => api.activities.upload(id, f, f.name));
  // Not useChange: leave the page first, so nothing refetches the deleted activity.
  const remove = useMutation({ mutationFn: () => api.activities.remove(id) });
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<unknown>(null);

  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorNote error={q.error} />;
  const a = withPending(q.data, pending.map(x => x.op));
  const order = dragOrder ?? a.steps.map(x => x.id);
  const canWrite = can(me.role, 'activity.write');
  // Delete and restore: owners, the person who created it, or an admin (the server checks the same).
  const steward = me.isAdmin || a.createdById === me.id || a.owners.some(o => o.id === me.id);
  const err = patch.error ?? stepErr ?? addStep.error ?? delStep.error ?? post.error ?? upload.error ?? remove.error;

  const move = (i: number, d: number) => {
    const next = [...order];
    const [moved] = next.splice(i, 1);
    next.splice(i + d, 0, moved!);
    runStep({ kind: 'order', ids: next });
  };
  const dragStart = () => setDragOrder(a.steps.map(x => x.id));
  // Let go: the new order is saved (and stays on screen while it saves).
  const dragEnd = () => {
    const ids = dragRef.current;
    setDragOrder(null);
    if (ids && ids.join() !== a.steps.map(x => x.id).join()) runStep({ kind: 'order', ids });
  };
  const submitStep = (e: FormEvent) => { e.preventDefault(); const v = stepDraft.trim(); if (v) addStep.mutate(v, { onSuccess: () => setStepDraft('') }); };
  const submitUpdate = (e: FormEvent) => { e.preventDefault(); const v = update.trim(); if (v) post.mutate(v, { onSuccess: () => setUpdate('') }); };
  const saveEdit = async (v: ActivityFormValue) => {
    setSaving(true); setSaveErr(null);
    try {
      if (Object.keys(v.changed).length) await patch.mutateAsync({ ...v.changed, version: editVersion });
      setEdit(false);
      toast(Object.keys(v.changed).length ? 'Saved' : 'Nothing changed');
    } catch (e) { setSaveErr(e); } finally { setSaving(false); }
  };

  const steps = (
    <div className="panel">
      <div className="sh" style={{ marginBottom: 6 }}><b>Steps{a.type === 'deliverable' ? '' : ' to be ready'}</b>{a.stepsTotal > 0 && <span className="mono">{a.stepsDone} of {a.stepsTotal}</span>}</div>
      {a.steps.length > 0 && <div style={{ marginBottom: 8 }}><Progress value={a.progress} color={a.progress >= 60 ? 'var(--grn)' : 'var(--amb)'} /></div>}
      <Reorder.Group axis="y" values={order} onReorder={setDragOrder} as="div" className="steps2">
        {order.map((sid, i) => {
          const st = a.steps.find(x => x.id === sid);
          if (!st) return null;
          return (
            <StepRow key={st.id} st={st} i={i} total={order.length} canWrite={canWrite}
              onToggle={() => runStep({ kind: 'done', id: st.id, done: !st.done })} onMove={d => move(i, d)} onRemove={() => delStep.mutate(st.id)}
              onDragStart={dragStart} onDrop={dragEnd} />
          );
        })}
      </Reorder.Group>
      {canWrite && (
        <form onSubmit={submitStep} style={{ marginTop: 8 }}>
          <input className="inp" value={stepDraft} onChange={e => setStepDraft(e.target.value)} placeholder="Add a step and press Enter" aria-label="Add a step" />
        </form>
      )}
    </div>
  );

  const updates = (limit?: number) => (
    <div className="panel">
      <div className="sh" style={{ marginBottom: 6 }}><b>{limit ? 'Latest updates' : 'Updates'}</b>{limit && a.updates.length > limit ? <button className="bt ghost" onClick={() => setTab('updates')}>All</button> : null}</div>
      {!limit && canWrite && (
        <form onSubmit={submitUpdate} className="col gap6" style={{ marginBottom: 8 }}>
          <textarea className="inp" rows={3} value={update} onChange={e => setUpdate(e.target.value)} placeholder="Post an update. Use @Name to tell someone." aria-label="Post an update" />
          <div><button className="bt pri" disabled={post.isPending || !update.trim()}>Post update</button></div>
        </form>
      )}
      {a.updates.length === 0 && <div className="xs mut">No updates yet.</div>}
      {a.updates.slice(0, limit ?? 200).map(u => (
        <div className="upd" key={u.id}>
          {u.author ? <Avatar p={u.author} /> : <span className="nav0 ai"><span className="aiorb" /></span>}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="body">{u.body}</div>
            <div className="xs mut">{u.author?.name ?? 'Assistant'} · {ago(u.createdAt)}{u.source !== 'manual' ? ` · ${u.source === 'team_check' ? 'team check' : u.source}` : ''}{u.kind === 'minutes' ? ' · minutes' : ''}</div>
          </div>
        </div>
      ))}
    </div>
  );

  const doDelete = () => { setGone(true); remove.mutate(undefined, {
    // Soft delete: the activity, its files and questions stay, and Undo brings it back.
    onSuccess: () => {
      if (inPanel) onClose?.(); else nav(`/tracks/${a.trackId}`);
      // Everything else refreshes; what is about this activity (its detail, files, brief) is not asked for again while
      // it leaves the screen (it is gone: the server would answer 404).
      void qc.invalidateQueries({ predicate: x => !x.queryKey.includes(a.id) });
      // This page is gone by the time Undo is pressed, so Undo calls the API directly.
      toast(`Deleted "${a.title}"`, {
        label: 'Undo',
        run: () => void api.activities.restore(a.id).then(() => qc.invalidateQueries()).then(() => nav(`/activities/${a.id}`), e => toast(e instanceof Error ? e.message : 'Could not restore it.')),
      });
    },
    onError: () => setGone(false),
  }); };
  const startEdit = () => { setEditVersion(a.version); setSaveErr(null); setEdit(true); };
  const sheets = (
    <>
      {remind && (
        <Sheet side={phone ? 'bottom' : 'right'} crumbs={<><span>{a.title}</span><span>/</span><b>Add a reminder</b></>} onClose={() => setRemind(false)} width={720}>
          <AddReminderForm me={me} activityId={a.id} autoFocus onSaved={() => setRemind(false)} />
        </Sheet>
      )}
      {subNew && space.data && q.data && (
        <NewActivity space={space.data} me={me} trackId={q.data.trackId} parent={{ id: q.data.id, title: q.data.title }} side={phone ? 'bottom' : 'right'} onClose={() => setSubNew(false)} />
      )}
      {edit && space.data && (
        <Sheet side={phone ? 'bottom' : 'right'} crumbs={<><span>{a.spaceName}</span><span>/</span><b>Edit activity</b></>} onClose={() => setEdit(false)}>
          <ActivityForm space={space.data} me={me} initial={a} submitLabel="Save" busy={saving} error={saveErr} onSubmit={v => void saveEdit(v)} onCancel={() => setEdit(false)} />
        </Sheet>
      )}
    </>
  );
  // Another activity from here: in the panel it takes the panel's place; on the full page it is that page.
  const ToAct = ({ id: to, children }: { id: string; children: React.ReactNode }) => (inPanel ? <ActLink id={to}>{children}</ActLink> : <Link to={`/activities/${to}`}>{children}</Link>);
  const askAi = () => panel.open({ activityId: a.id, spaceId: a.spaceId, trackId: a.trackId, label: a.title });
  // One line: the kind (its colour dot and name), then where it is (Space › Track › the activity it is part of),
  // cut short with an ellipsis. Links on the full page; plain words in the panel (the page behind stays put).
  const sep = <span className="aksep" aria-hidden="true">›</span>;
  const crumb = (
    <div className="crumbs akc">
      <span className="akk"><i style={{ background: KIND_COLOR[a.type] }} />{TYPE_LABELS[a.type]}</span>
      <span className="aksep" aria-hidden="true">·</span>
      <span className="akp">
        {inPanel ? <span>{a.spaceName}</span> : <Link to={`/spaces/${a.spaceId}`}>{a.spaceName}</Link>}{sep}
        {inPanel ? <span>{a.trackName}</span> : <Link to={`/tracks/${a.trackId}`}>{a.trackName}</Link>}
        {a.partOf && <>{sep}{inPanel ? <span>{a.partOf.title}</span> : <Link to={`/activities/${a.partOf.id}`}>{a.partOf.title}</Link>}</>}
      </span>
    </div>
  );
  // The panel's own top (desktop): the crumb, Open full page, its AI sphere (asking about this activity), ×.
  const panelTop = inPanel && (
    <div className="actp-top">
      {crumb}
      <Link className="ib" to={`/activities/${a.id}`} onClick={() => onClose?.()} aria-label="Open full page" title="Open full page"><IconExpand /></Link>
      <AiSphere sm onClick={askAi} about={a.title} />
      <button type="button" className="ib" aria-label="Close the activity" title="Close" onClick={() => onClose?.()}><IconX /></button>
    </div>
  );
  // Remind, Edit, and the rest in a quiet "more" menu: Delete, with a confirm (it can be undone for 30 days).
  const actions = (
    <div className="hact">
      {(a.type === 'meeting' || a.type === 'workshop') && <button type="button" className="bt" onClick={() => panel.open({ activityId: a.id, spaceId: a.spaceId, label: a.title, prompt: `Brief me for ${a.title}`, send: true })}>Brief me, 3 min</button>}
      {(a.type === 'meeting' || a.type === 'workshop') && <Link className="bt" to={`/meetings/${a.id}`}>Minutes and recording</Link>}
      <button type="button" className="bt" onClick={() => setRemind(true)}>Remind</button>
      {canWrite && <button type="button" className="bt" onClick={startEdit} disabled={!space.data}>Edit</button>}
      {canWrite && steward && (
        <MoreMenu label="More actions" className="actmore">
          {close => askDelete ? (
            <div className="morecfm" role="none">
              <span>Delete this {TYPE_LABELS[a.type].toLowerCase()}? Undo brings it back, and Settings, Deleted keeps it for 30 days.</span>
              <div className="rowc" style={{ gap: 6 }}>
                <button type="button" role="menuitem" className="bt pri sm" onClick={() => { close(); setAskDelete(false); doDelete(); }}>Delete</button>
                <button type="button" role="menuitem" className="bt sm" onClick={() => setAskDelete(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <button type="button" role="menuitem" className="moreit danger" onClick={() => setAskDelete(true)}><IconTrash />Delete</button>
          )}
        </MoreMenu>
      )}
    </div>
  );
  // On a phone the sheet's controls sit in the activity's own top bar (no Back: × closes the sheet).
  const sheetTop = inPanel ? {
    lead: <>
      <Link className="ph-nb" to={`/activities/${a.id}`} onClick={() => onClose?.()} aria-label="Open full page" title="Open full page"><IconExpand /></Link>
      <button type="button" className="aibtn sm" onClick={askAi}><span className="o"><Orb size={12} /></span>Ask AI</button>
    </>,
    close: <button type="button" className="ph-nb" aria-label="Close the activity" onClick={() => onClose?.()}><IconX /></button>,
  } : undefined;
  if (phone) return (
    <div data-fetched={q.dataUpdatedAt} className={inPanel ? 'actp-phone' : undefined}>
      <PhoneActivity sheet={sheetTop} a={a} me={me} files={files.data ?? []} canWrite={canWrite} err={err} prepRisk={prepAtRisk(a) && !redStatus(a)}
        onToggle={st => runStep({ kind: 'done', id: st.id, done: !st.done })}
        onAddStep={(t, done) => addStep.mutate(t, { onSuccess: done })} onPost={(t, done) => post.mutate(t, { onSuccess: done })}
        onUpload={f => upload.mutate(f)} onEdit={canWrite && space.data ? startEdit : undefined} onRemind={() => setRemind(true)} onDelete={canWrite && steward ? doDelete : undefined}
        onAddSub={canWrite && space.data ? () => setSubNew(true) : undefined} />
      {sheets}
    </div>
  );
  const dropFiles = (list: FileList) => { for (const f of [...list]) upload.mutate(f, { onSuccess: () => toast(`Added ${f.name}`) }); };
  return (
    <div ref={root} tabIndex={inPanel ? -1 : undefined} className={`actpage${inPanel ? ' actpanel' : ''}${drop ? ' drop' : ''}`} data-fetched={q.dataUpdatedAt} aria-label={inPanel ? `Activity: ${a.title}` : undefined} role={inPanel ? 'region' : undefined}
      onKeyDown={inPanel ? e => { if (e.key === 'Escape' && !e.defaultPrevented) { e.stopPropagation(); onClose?.(); } } : undefined}
      onDragOver={e => { if (canWrite && e.dataTransfer.types.includes('Files')) { e.preventDefault(); setDrop(true); } }}
      onDragLeave={e => { if (e.currentTarget === e.target) setDrop(false); }}
      onDrop={e => { if (!canWrite) return; e.preventDefault(); setDrop(false); dropFiles(e.dataTransfer.files); }}>
      {drop && <div className="dropov" aria-hidden="true"><div className="dropin"><IconClip /><b>Drop to add to {a.title}</b></div></div>}
      {panelTop}
      {/* The full page: one header row (the crumb, then the actions), level with the shell's AI sphere at its right. */}
      {!inPanel && <div className="acthd">{crumb}{actions}</div>}
      <div className="hdr acttl">
        <div className="htl">
          {inPanel ? <h2 className="actp-h">{a.title}</h2> : <h1>{a.title}</h1>}
          <StatusPill status={a.status} overdue={a.isOverdue} />
          {/* One red pill: when the activity itself is at risk, blocked or late, prep at risk stays in the Prep fact. */}
          {prepAtRisk(a) && !redStatus(a) && <span className="pl red">Prep at risk</span>}
          {a.isMilestone && <span className="rowc xs mut" style={{ gap: 6 }}><Diamond />Milestone</span>}
          {a.isClientRequest && <span className="lbl client">Client request</span>}
          {a.waitingOn && <span className="lbl wait">Waiting on {a.waitingOn.name}</span>}
        </div>
        {inPanel && actions}
      </div>
      <div className="metas">
        {(a.type === 'meeting' || a.type === 'workshop') && <div><b>When</b><span>{when(a)}</span></div>}
        {a.location && <div><b>Where</b><span>{a.location}</span></div>}
        {(a.dueDate || (a.type !== 'meeting' && a.type !== 'workshop')) && <div><b>Due</b><span style={{ color: a.isOverdue ? 'var(--red-tx)' : undefined }}>{a.dueDate ? `${dayLabel(a.dueDate)}${a.dueTime ? ` ${a.dueTime}` : ''}` : 'No date'}</span></div>}
        <div><b>Owners</b><span>{a.owners.map(o => o.name.split(' ')[0]).join(', ') || 'None'}</span></div>
        {a.attendees.length > 0 && <div><b>With</b><span>{a.attendees.slice(0, 3).map(x => x.name).join(', ')}{a.attendees.length > 3 ? ` +${a.attendees.length - 3}` : ''}</span></div>}
        {a.requestedBy && <div><b>Requested by</b><span>{a.requestedBy.name}</span></div>}
        {a.waitingOn && <div><b>Waiting on</b><span>{a.waitingOn.name}{a.waitingSince ? ` · since ${ago(a.waitingSince)}` : ''}</span></div>}
        {a.partOf && <div><b>Part of</b><span><ToAct id={a.partOf.id}>{a.partOf.title}</ToAct></span></div>}
        {a.cameFrom && <div><b>From</b><span><ToAct id={a.cameFrom.id}>{a.cameFrom.title}</ToAct></span></div>}
        {a.leadsTo.length > 0 && <div><b>Follow-ups</b><span>{a.leadsTo.map((x, i) => <span key={x.id}>{i ? ', ' : ''}<ToAct id={x.id}>{x.title}</ToAct></span>)}</span></div>}
        {a.needsPrep && <div><b>Prep</b><span className="rowc prepfact"><span className="bar"><Progress value={a.progress} color={a.progress >= 60 ? 'var(--grn)' : 'var(--red)'} /></span><span>{a.stepsDone} of {a.stepsTotal} ready{prepAtRisk(a) ? <>, <b className="riskword">at risk</b></> : null}</span></span></div>}
      </div>
      <div className="tabs3" role="tablist">
        <button role="tab" aria-selected={tab === 'overview'} className={tab === 'overview' ? 'on' : ''} onClick={() => setTab('overview')}>{tab === 'overview' && <motion.span layoutId="act-tab" className="tabmark" transition={spring.snappy} />}Overview</button>
        <button role="tab" aria-selected={tab === 'updates'} className={tab === 'updates' ? 'on' : ''} onClick={() => setTab('updates')}>{tab === 'updates' && <motion.span layoutId="act-tab" className="tabmark" transition={spring.snappy} />}Updates<span className="ct">{a.updates.length || ''}</span>{tabNew.updates && <NewDot />}</button>
        <button role="tab" aria-selected={tab === 'minutes'} className={tab === 'minutes' ? 'on' : ''} onClick={() => setTab('minutes')}>{tab === 'minutes' && <motion.span layoutId="act-tab" className="tabmark" transition={spring.snappy} />}Minutes<span className="ct">{a.updates.filter(x => x.kind === 'minutes').length || ''}</span>{tabNew.minutes && <NewDot />}</button>
        <button role="tab" aria-selected={tab === 'files'} className={tab === 'files' ? 'on' : ''} onClick={() => setTab('files')}>{tab === 'files' && <motion.span layoutId="act-tab" className="tabmark" transition={spring.snappy} />}Files<span className="ct">{files.data?.length || ''}</span>{tabNew.files && <NewDot />}</button>
      </div>
      {err ? <ErrorNote error={err} onReload={() => patch.reset()} /> : null}
      {tab === 'overview' && (
        <div className="cols3">
          <Glide className="l">
            <BriefPanel kind="activity" id={a.id} />
            {steps}
            <SubActivities a={a} canWrite={canWrite} onAddFull={space.data ? () => setSubNew(true) : undefined} />
          </Glide>
          <Glide className="r">
            <AssistantAsks activityId={a.id} />
            <CheckIns activityId={a.id} />
            <div className="panel col gap12">
              <label className="fld">Status
                <select className="inp" value={a.status} disabled={!canWrite} onChange={e => patch.mutate({ status: e.target.value as ActivityStatus })}>
                  {ACTIVITY_STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                </select>
              </label>
              {canWrite ? (
                <>
                  <div className="togrow"><Toggle on={a.isMilestone} label="Milestone" onChange={v => patch.mutate({ isMilestone: v })} /><span>Milestone</span></div>
                  <div className="togrow"><Toggle on={a.needsPrep} label="Needs prep" onChange={v => patch.mutate({ needsPrep: v })} /><span>Needs prep</span></div>
                </>
              ) : <div className="xs mut">You can read this and ask the assistant about it. Changes are made by the team.</div>}
              {a.tags.length > 0 && <div className="xs mono mut">{a.tags.map(t => `#${t}`).join(' ')}</div>}
              <div className="xs mut">Created {ago(a.createdAt)} · updated {ago(a.updatedAt)}{a.updatedBy ? ` by ${a.updatedBy.name}` : ''}</div>
            </div>
            {updates(3)}
          </Glide>
        </div>
      )}
      {tab === 'updates' && updates()}
      {tab === 'minutes' && (
        <div className="panel">
          {a.updates.filter(x => x.kind === 'minutes').length ? a.updates.filter(x => x.kind === 'minutes').map(m => (
            <div className="upd" key={m.id}><span className="ubadge" aria-hidden="true">MIN</span>
              <div style={{ flex: 1, minWidth: 0 }}><div className="body">{m.body}</div><div className="xs mut">{m.author?.name ?? 'Assistant'} · {ago(m.createdAt)}{m.source === 'recording' ? ' · recorded in app' : ''}</div></div>
            </div>
          )) : <div className="empty0"><b>No minutes yet.</b><span className="xs mut">Minutes filed for this {a.type} show here. <Link to={`/record?activity=${a.id}`}>Record it</Link>, or <Link to={`/meetings?log=1&activity=${a.id}`}>log the minutes by hand</Link>.</span></div>}
        </div>
      )}
      {tab === 'files' && (
        <div className="panel fpan">
          {canWrite && <button className="dropz" onClick={() => fileInput.current?.click()}><IconClip />Drop files here, or choose them</button>}
          <span className="xs mut">Files here also show in the {a.trackName} track's Files.</span>
          <input ref={fileInput} type="file" hidden onChange={e => { const f = e.target.files?.[0]; if (f) upload.mutate(f, { onSuccess: () => toast(`Added ${f.name}`) }); e.target.value = ''; }} />
          {upload.isPending && <div className="rowc xs mut"><span className="spin" />Uploading</div>}
          <FileList files={files.data ?? []} />
        </div>
      )}
      {sheets}
    </div>
  );
}

/** One step: tick (the check draws), drag by the handle or use the arrows to move, remove. */
function StepRow({ st, i, total, canWrite, onToggle, onMove, onRemove, onDragStart, onDrop }: {
  st: Step; i: number; total: number; canWrite: boolean; onToggle: () => void; onMove: (d: number) => void; onRemove: () => void; onDragStart: () => void; onDrop: () => void;
}) {
  const drag = useDragControls();
  return (
    <Reorder.Item value={st.id} as="div" className={`steprow${st.done ? ' done' : ''}`} dragListener={false} dragControls={drag} onDragEnd={onDrop}
      transition={{ type: 'spring', stiffness: 520, damping: 38 }} whileDrag={{ scale: 1.01, boxShadow: '0 8px 24px -10px rgba(0,0,0,.3)' }}>
      {canWrite && <span className="grip" aria-hidden="true" onPointerDown={e => { onDragStart(); drag.start(e); }} title="Drag to move">⋮⋮</span>}
      <button className={`chk${st.done ? ' on' : ''}`} aria-label={st.done ? 'Mark not done' : 'Mark done'} aria-pressed={st.done} disabled={!canWrite} onClick={onToggle}>
        {st.done ? <DrawnCheck size={14} /> : null}
      </button>
      <span className="n">{i + 1}</span>
      <span className="t">{st.title}{st.doneAt ? <span className="xs mut"> · done {ago(st.doneAt)}</span> : null}</span>
      {st.owner && <Avatar p={st.owner} />}
      {canWrite && (
        <span className="mv">
          <button aria-label="Move up" disabled={i === 0} onClick={() => onMove(-1)}><IconUp /></button>
          <button aria-label="Move down" disabled={i === total - 1} onClick={() => onMove(1)}><IconDown /></button>
          <button aria-label="Remove step" onClick={onRemove}><IconX /></button>
        </span>
      )}
    </Reorder.Item>
  );
}

const IconTrash = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" /></svg>;
const IconExpand = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 4h6v6M10 20H4v-6M20 4l-7 7M4 20l7-7" /></svg>;
